import { rangeChange, roundsToZero, seriesValues } from "../glance";
import { PointIcon } from "../icon";
import { boundPoint, Frame, key, Note, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

const RANGES = new Set(["1h", "6h", "24h", "7d", "30d"]);

/** The line and the area under it in a 100 × 32 box, scaled to the series' own low and high. */
function paths(points: { t: number; v: number | null }[]) {
  const present = points.filter((p): p is { t: number; v: number } => p.v !== null);
  const first = present[0];
  const last = present.at(-1);
  if (present.length < 2 || !first || !last) return undefined;
  const span = last.t - first.t || 1;
  const lo = Math.min(...present.map((p) => p.v));
  const hi = Math.max(...present.map((p) => p.v));
  const xy = (p: { t: number; v: number }) =>
    [((p.t - first.t) / span) * 100, 29 - ((p.v - lo) / (hi - lo || 1)) * 26] as const;
  const line = present.map((p, i) => `${i ? "L" : "M"}${xy(p)[0].toFixed(1)} ${xy(p)[1].toFixed(1)}`).join("");
  return { line, area: `${line}L100 32L0 32Z`, end: xy(last) };
}

/**
 * sparkline: the current value large, its change over the range, and a small area line of the range under it
 * (VisionPlatform widget-library-2 WLB-11). No axes.
 */
export function Sparkline({ widget, input }: WidgetProps) {
  const fmt = useWidgetText();
  const bound = boundPoint(widget, input);
  const point = bound?.point;
  const name = widget.title ?? point?.name ?? "";
  const cfg = widget.config ?? {};
  const range = typeof cfg.range === "string" && RANGES.has(cfg.range) ? cfg.range : "24h";
  const decimals = typeof cfg.decimals === "number" ? cfg.decimals : (point?.decimals ?? 0);
  const v = point?.latest?.value;
  if (!point || typeof v !== "number") {
    return (
      <Frame widget={widget} state="empty" title={name} icon={<PointIcon point={point} />}>
        <p aria-label={`${name}: ${fmt.t("state.noData")}`} className="text-gray-700">
          {fmt.t("state.noData")}
        </p>
      </Frame>
    );
  }
  const history = bound.binding.datapoint
    ? input.history?.[key(bound.binding.device, bound.binding.datapoint)]
    : undefined;
  const text = fmt.value(v, point, decimals);
  const change = rangeChange(history, v);
  const rangeText = fmt.t(`range.${range}`);
  const changeText =
    change === undefined
      ? undefined
      : roundsToZero(change, decimals)
        ? fmt.t("sparkline.same", { range: rangeText })
        : fmt.t("sparkline.change", {
            change: `${change > 0 ? "▲" : "▼"} ${fmt.value(Math.abs(change), point, decimals)}`,
            range: rangeText,
          });
  const offline = bound.device?.state === "offline";
  const drawn = paths(seriesValues(history));
  return (
    <Frame widget={widget} state={offline ? "offline" : "normal"} title={name} icon={<PointIcon point={point} />}>
      <p aria-label={`${name}: ${text}`} className="text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">
        {text}
      </p>
      {changeText && <p className="text-xs text-gray-700">{changeText}</p>}
      {drawn && (
        <svg
          aria-hidden="true"
          viewBox="0 0 100 32"
          preserveAspectRatio="none"
          // It fills the height the widget has, at least 40 px, right under the numbers (owner's reports #11, #27).
          className={`mt-1 min-h-10 w-full flex-1 overflow-visible ${offline ? "text-gray-400" : "text-brand"}`}
        >
          <path d={drawn.area} fill="currentColor" fillOpacity="0.1" />
          <path
            d={drawn.line}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
      {offline && <Note tone="warn">{fmt.t("state.offline")}</Note>}
    </Frame>
  );
}
